import { REF_ID_MAX_LENGTH, REF_ID_PATTERN } from './extension-point-ref.js';
const POINT_ID = new RegExp(REF_ID_PATTERN);
/**
 * True when `value` is an extension point id: a string of at most 96 characters that matches
 * `<owner>/<name>.vN` (`REF_ID_PATTERN`, the copy of `EXTENSION_POINT_ID_PATTERN`). The length is
 * checked before the pattern. Any other value, a non-string included, answers `false`. Pure.
 */
export function isExtensionPointId(value) {
    return typeof value === 'string' && value.length <= REF_ID_MAX_LENGTH && POINT_ID.test(value);
}
/**
 * The owner slug of a well-formed point id: the part before the `/`. The caller passes an id that
 * {@link isExtensionPointId} accepted; for a string with no `/` the answer is the whole string. Pure.
 */
export function pointOwner(point) {
    const slash = point.indexOf('/');
    return slash === -1 ? point : point.slice(0, slash);
}
/**
 * The `{ app, surface }` copy of an entry whose `app` and `surface` are both strings, else `null`.
 * Reads each key once through `Reflect.get`, so a getter cannot answer a string to the check and
 * another value to the copy.
 */
function implementerOf(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const app = Reflect.get(value, 'app');
    const surface = Reflect.get(value, 'surface');
    return typeof app === 'string' && typeof surface === 'string' ? Object.freeze({ app, surface }) : null;
}
/** Code-unit order of two strings: negative, zero or positive. */
function compareCodeUnits(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
/**
 * A host's implementer answer as the runtime trusts it.
 *
 * Input: any value. Output: a new frozen array of frozen `{ app, surface }` copies, one per distinct
 * app and surface pair, sorted by app, then by surface, by code unit. A value that is not an array
 * answers `[]`. An entry that is not an object with a string `app` and a string `surface` is dropped;
 * extra keys on an entry are dropped. Pure: the input is read, never changed.
 */
export function normaliseImplementers(answer) {
    if (!Array.isArray(answer))
        return Object.freeze([]);
    const seen = new Set();
    const out = [];
    const entries = answer;
    for (const entry of entries) {
        const implementer = implementerOf(entry);
        if (implementer === null)
            continue;
        const key = JSON.stringify([implementer.app, implementer.surface]);
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(implementer);
    }
    out.sort((a, b) => compareCodeUnits(a.app, b.app) || compareCodeUnits(a.surface, b.surface));
    return Object.freeze(out);
}
/**
 * The implementer a mount by point picks (D9).
 *
 * Inputs: the host's answer (any value, read through {@link normaliseImplementers}), a well-formed
 * point id, and the app the consumer named, or `undefined`.
 *
 * Output: the implementer of `app` when it is given, else the implementer of the point owner
 * ({@link pointOwner}). When that app has two or more implementing surfaces, the first surface name
 * by code unit wins. `null` when the answer holds no implementer of that app: a third-party
 * implementer is picked only when the consumer names its app. Pure.
 */
export function pickImplementer(answer, point, app) {
    const wanted = app ?? pointOwner(point);
    return normaliseImplementers(answer).find((entry) => entry.app === wanted) ?? null;
}
//# sourceMappingURL=point-target.js.map